import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {
  SearchScoreCardClient,
  GetScoreCardClient,
  DeleteScoreCardClient,
  UpdateScoreCardClient,
  CreateScoreCardClient,
  GetScoreCardDaysClient,
  UpdateScoreCardDaysClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const searchScoreCardClient = new SearchScoreCardClient(SERVICE_URL)
const getScoreCardClient = new GetScoreCardClient(SERVICE_URL)
const createScoreCardClient = new CreateScoreCardClient(SERVICE_URL)
const deleteScoreCardClient = new DeleteScoreCardClient(SERVICE_URL)
const updateScoreCardClient = new UpdateScoreCardClient(SERVICE_URL)
const getScoreCardDaysClient = new GetScoreCardDaysClient(SERVICE_URL)
const updateScoreCardDaysClient = new UpdateScoreCardDaysClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  scorecard: null,
  detail: null,
  scorecardDay: null,
}

// Async Thunks
export const scorecardGetAll = createAsyncThunk('scorecard/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchScoreCardClient.search(
      params.name,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setScorecard(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const scorecardGetId = createAsyncThunk('scorecard/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getScoreCardClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const scorecardDelete = createAsyncThunk('scorecard/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteScoreCardClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const scorecardCreate = createAsyncThunk('scorecard/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createScoreCardClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const scorecardUpdate = createAsyncThunk('scorecard/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateScoreCardClient.update(params)
    dispatch(setRedirect(response || params))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

export const scorecardDayGetAll = createAsyncThunk(
  'scorecard/dayGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getScoreCardDaysClient.search(params)
      dispatch(setScorecardDay(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const scorecardDayUpdate = createAsyncThunk(
  'scorecard/dayUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateScoreCardDaysClient.update(params)
      dispatch(setRedirect(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const scorecardSlice = createSlice({
  name: 'scorecard',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setScorecard: (state, action) => {
      state.scorecard = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setScorecardDay: (state, action) => {
      state.scorecardDay = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('scorecard/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setScorecard,
  setDetail,
  setDeleted,
  setRedirect,
  setScorecardDay,
  resetState,
} = scorecardSlice.actions

// Selectors
export const selectScorecardLoading = (state) => state.scorecard.loading
export const selectScorecardError = (state) => state.scorecard.error
export const selectScorecard = (state) => state.scorecard.scorecard
export const selectScorecardDetail = (state) => state.scorecard.detail
export const selectScorecardDeleted = (state) => state.scorecard.isDeleted
export const selectScorecardRedirect = (state) => state.scorecard.isRedirect
export const selectScorecardDay = (state) => state.scorecard.scorecardDay

export default scorecardSlice.reducer
